import { useEffect, useMemo, useState } from 'react'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'

import { useLocation } from 'react-router-dom'
import Payment from '../components/Payment'
import { getSharedConfig } from '../get-config'
import useOrder from '../data/useOrder'
import {
  PaymentOrderDocument,
  PaymentOrderQuery,
  SetupRecurringPaymentMethodDocument,
  SetupRecurringPaymentMethodMutation,
  SetupRecurringPaymentMethodMutationVariables,
} from '../graphql'
import SkeletonPayment from '../components/Payment/Skeletons/SkeletonPayment'
import useMutationWithErrorHandling from '../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import sessionUtils from '../data/session-utils'
import { useIntl } from '../localization/react'
import { CashPayment } from '../components/Payment/CashPayment'
import { BlockingError } from '../lib/custom-errors'

const PaymentScreenWrapper = () => {
  const intl = useIntl()
  const location = useLocation()
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | undefined>()
  const { paymentInProgress } = getPhoenixUrlParams(location.search)

  const { orderData, isOrderLoading } = useOrder<PaymentOrderQuery>(PaymentOrderDocument)
  const [setupRecurringPayment, { data: recurringPaymentData }] = useMutationWithErrorHandling<
    SetupRecurringPaymentMethodMutation,
    SetupRecurringPaymentMethodMutationVariables
  >(SetupRecurringPaymentMethodDocument, {})

  useEffect(() => {
    if (!orderData) return

    if (!orderData.order.payment && !orderData.order.isConfirmable) {
      throw new BlockingError('There was no payment method available', intl.noPaymentMethodAvailable())
    }
  }, [orderData])

  useEffect(() => {
    if (setupIntentClientSecret) return

    const sessionStoredSetupIntentClientSecret = sessionUtils.getSetupIntentClientSecret()

    if (sessionStoredSetupIntentClientSecret) {
      setSetupIntentClientSecret(sessionStoredSetupIntentClientSecret)
    } else if (orderData && orderData.order.payment && recurringPaymentData) {
      const serverSetupIntentClientSecret =
        orderData.order.payment.canSetupRecurringPaymentMethod &&
        recurringPaymentData.setupRecurringPaymentMethod?.__typename === 'Payment' &&
        recurringPaymentData.setupRecurringPaymentMethod.paymentSetupIntent?.clientSecret

      if (!serverSetupIntentClientSecret) {
        throw new Error('Missing setup intent client secret from server response')
      }

      sessionUtils.setSetupIntentClientSecret(serverSetupIntentClientSecret)
      setSetupIntentClientSecret(serverSetupIntentClientSecret)
    }
  }, [orderData, recurringPaymentData])

  const stripePromise = useMemo(() => loadStripe(getSharedConfig().STRIPE_API_PUBLISHABLE_KEY), [])

  useEffect(() => {
    if (!orderData?.order?.payment?.canSetupRecurringPaymentMethod || recurringPaymentData || paymentInProgress) {
      return
    }

    setupRecurringPayment({
      variables: { paymentId: orderData.order.payment.id },
    })
  }, [orderData?.order?.payment?.id])

  if (
    isOrderLoading ||
    !orderData ||
    (orderData?.order?.payment?.canSetupRecurringPaymentMethod && !recurringPaymentData && !setupIntentClientSecret) ||
    (orderData?.order.payment?.canSetupRecurringPaymentMethod && paymentInProgress && !setupIntentClientSecret)
  ) {
    return <SkeletonPayment />
  }

  if (recurringPaymentData?.setupRecurringPaymentMethod.__typename === 'ExpectedErrors') {
    throw new Error(recurringPaymentData.setupRecurringPaymentMethod.errors.map((e) => e.message).join('\n'))
  }

  if (!orderData.order.payment && orderData.order.isConfirmable) {
    return <CashPayment />
  }

  const clientSecret = orderData.order.payment?.canSetupRecurringPaymentMethod
    ? recurringPaymentData?.setupRecurringPaymentMethod?.paymentSetupIntent?.clientSecret
    : orderData.order.payment?.paymentIntent?.clientSecret

  const customPaymentMethods = []

  if (orderData.order.isConfirmable && getSharedConfig().OFFLINE_PAYMENT_CUSTOM_STRIPE_PAYMENT_METHOD_ID) {
    customPaymentMethods.push({
      id: getSharedConfig().OFFLINE_PAYMENT_CUSTOM_STRIPE_PAYMENT_METHOD_ID,
      options: { type: 'static', subtitle: intl.offlinePaymentSubtitle() },
    } as const)
  }

  return (
    <PaymentScreen
      stripePromise={stripePromise}
      customPaymentMethods={customPaymentMethods}
      clientSecret={setupIntentClientSecret ?? clientSecret ?? ''}
      customerSessionClientSecret={orderData.order.payment?.customerSessionClientSecret ?? ''}
    />
  )
}

interface Props {
  stripePromise: Promise<Stripe | null>
  clientSecret: string
  customPaymentMethods?: Array<{ id: string; options: { type: 'static'; subtitle?: string } }>
  customerSessionClientSecret?: string
}

const PaymentScreen = ({
  stripePromise,
  clientSecret,
  customPaymentMethods = [],
  customerSessionClientSecret,
}: Props) => (
  <Elements
    stripe={stripePromise}
    options={{
      clientSecret,
      customPaymentMethods,
      customerSessionClientSecret,
      loader: 'always',
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#198073',
        },
      },
    }}
  >
    <Payment />
  </Elements>
)

export default PaymentScreenWrapper
